/**
 * Pure helpers for Food Safety reports week-grid + Free→Starter paywall copy.
 */

import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isoWeek from 'dayjs/plugin/isoWeek'
import {
  FOOD_SAFETY_REPORT_CELL_STATUS,
  FOOD_SAFETY_REPORT_EXCEPTION_STATE,
  FOOD_SAFETY_REPORT_EXCEPTION_STATE_LABELS,
  FOOD_SAFETY_REPORTS_PAYWALL_REASON,
  FOOD_SAFETY_SECTION,
  FOOD_SAFETY_WEEK_PRESET,
} from 'src/constants/foodSafety'

dayjs.extend(customParseFormat)
dayjs.extend(isoWeek)

/** "21–27 Sep 2026", "28 Sep – 4 Oct 2026", "29 Dec 2025 – 4 Jan 2026". */
export function formatFoodSafetyWeekRangeLabel(weekStart, weekEnd) {
  if (!weekStart || !weekEnd) return ''
  const start = dayjs(weekStart, 'YYYY-MM-DD')
  const end = dayjs(weekEnd, 'YYYY-MM-DD')
  if (!start.isValid() || !end.isValid()) return `${weekStart} – ${weekEnd}`
  if (start.year() !== end.year()) {
    return `${start.format('D MMM YYYY')} – ${end.format('D MMM YYYY')}`
  }
  if (start.month() === end.month()) {
    return `${start.format('D')}–${end.format('D MMM YYYY')}`
  }
  return `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`
}

export function shiftFoodSafetyWeekStart(weekStart, weeks) {
  if (!weekStart) return null
  const start = dayjs(weekStart, 'YYYY-MM-DD')
  if (!start.isValid()) return null
  return start.add(Number(weeks) || 0, 'week').format('YYYY-MM-DD')
}

export function formatFoodSafetyLogCountLabel(logCount) {
  const n = Number(logCount) || 0
  if (n <= 0) return null
  return n === 1 ? '1 log' : `${n} logs`
}

export function foodSafetyReportRowFractionLabel(weekSummary) {
  if (!weekSummary) return '—'
  if (weekSummary.fractionLabel) return weekSummary.fractionLabel
  return formatFoodSafetyLogCountLabel(weekSummary.logCount) || '—'
}

/** Report rows in the same order as the Home tiles. */
export const FOOD_SAFETY_REPORT_ROW_ORDER = Object.freeze([
  FOOD_SAFETY_SECTION.FRIDGES_FREEZERS,
  FOOD_SAFETY_SECTION.COOKING_REHEATING,
  FOOD_SAFETY_SECTION.COOLING,
  FOOD_SAFETY_SECTION.HOT_HOLDING,
  FOOD_SAFETY_SECTION.COLD_HOLDING,
  FOOD_SAFETY_SECTION.DELIVERIES,
  FOOD_SAFETY_SECTION.PROBE_CALIBRATION,
  FOOD_SAFETY_SECTION.DISHWASHERS,
  FOOD_SAFETY_SECTION.LEGACY_EQUIPMENT,
  FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS,
])

/** Rows Express sent, in Home order; unknown sections are dropped. */
export function sortFoodSafetyReportRows(rows) {
  const rank = new Map(FOOD_SAFETY_REPORT_ROW_ORDER.map((key, i) => [key, i]))
  return (rows || [])
    .filter(row => rank.has(row?.section))
    .sort((a, b) => rank.get(a.section) - rank.get(b.section))
}

export const FOOD_SAFETY_REPORT_OUT_OF_SERVICE_MARK = '⊘'

/**
 * Grid key. Exception entries draw the cell's corner dot instead of a mark.
 */
export const FOOD_SAFETY_REPORT_LEGEND = Object.freeze([
  { key: 'not_required', mark: 'N/R', label: 'Reviewed - Not Required' },
  { key: 'completed', mark: '✓', label: 'All due checks done' },
  { key: 'logged', mark: '•', label: 'Logged as you go' },
  { key: 'partial', mark: '½', label: 'Partly done' },
  { key: 'missed', mark: '!', label: 'Missed' },
  { key: 'not_due', mark: '·', label: 'Not due yet' },
  {
    key: 'out_of_service',
    mark: FOOD_SAFETY_REPORT_OUT_OF_SERVICE_MARK,
    label: 'Out of service',
  },
  { key: 'closed', mark: 'Closed', label: 'Closed' },
  { key: 'none', mark: '—', label: 'Nothing due' },
  { key: 'locked', mark: '✕', label: 'Outside Lite history' },
  {
    key: 'exception_actioned',
    exceptionState: FOOD_SAFETY_REPORT_EXCEPTION_STATE.ACTIONED,
    label:
      FOOD_SAFETY_REPORT_EXCEPTION_STATE_LABELS[
        FOOD_SAFETY_REPORT_EXCEPTION_STATE.ACTIONED
      ],
  },
  {
    key: 'exception_unresolved',
    exceptionState: FOOD_SAFETY_REPORT_EXCEPTION_STATE.UNRESOLVED,
    label:
      FOOD_SAFETY_REPORT_EXCEPTION_STATE_LABELS[
        FOOD_SAFETY_REPORT_EXCEPTION_STATE.UNRESOLVED
      ],
  },
])

/** Grid key; "Outside Lite history" only where the window applies. */
export function listFoodSafetyReportLegend({ historyUnlocked = false } = {}) {
  return FOOD_SAFETY_REPORT_LEGEND.filter(
    item => !(historyUnlocked && item.key === 'locked'),
  )
}

/**
 * One key entry. A mark that is already the caption (the word Closed) is
 * shown once. Other entries stay a symbol plus a caption.
 */
export function foodSafetyReportLegendParts(item) {
  if (!item) return { mark: '', label: '' }
  if (item.exceptionState) {
    return { mark: '', label: item.label || '', dot: item.exceptionState }
  }
  const mark = item.mark || ''
  const label = item.label || ''
  if (mark && mark === label) return { mark: '', label }
  return { mark, label }
}

/** actioned / unresolved for the cell's corner dot, or null. */
export function foodSafetyReportCellExceptionState(cell) {
  const state = cell?.exceptionState
  return state === FOOD_SAFETY_REPORT_EXCEPTION_STATE.ACTIONED ||
    state === FOOD_SAFETY_REPORT_EXCEPTION_STATE.UNRESOLVED
    ? state
    : null
}

/** "1 exception · action recorded" / "2 exceptions · 1 unresolved", or null. */
export function foodSafetyReportCellExceptionLabel(cell) {
  const raised = Number(cell?.exceptionCount) || 0
  if (!raised) return null
  const open = Number(cell?.unresolvedExceptionCount) || 0
  const head = raised === 1 ? '1 exception' : `${raised} exceptions`
  if (!open) {
    return `${head} · ${raised === 1 ? 'action' : 'actions'} recorded`
  }
  return `${head} · ${open} unresolved`
}

/**
 * Display mark for a report cell (calm, no emoji fireworks).
 * Accepts a status string or a full cell — scheduled partials show `1/3`.
 */
export function foodSafetyReportCellMark(statusOrCell) {
  const cell =
    statusOrCell && typeof statusOrCell === 'object' ? statusOrCell : null
  const status = cell ? cell.status : statusOrCell
  if (
    status === FOOD_SAFETY_REPORT_CELL_STATUS.NONE &&
    Number(cell?.notRequiredCount) > 0
  )
    return 'N/R'
  switch (status) {
    case FOOD_SAFETY_REPORT_CELL_STATUS.COMPLETED:
      return '✓'
    case FOOD_SAFETY_REPORT_CELL_STATUS.LOGGED:
      return '•'
    case FOOD_SAFETY_REPORT_CELL_STATUS.MISSED:
      return '!'
    case FOOD_SAFETY_REPORT_CELL_STATUS.PARTIAL: {
      const due = Number(cell?.dueCount) || 0
      const done = Number(cell?.completedCount) || 0
      if (due > 0) return `${done}/${due}`
      return '½'
    }
    case FOOD_SAFETY_REPORT_CELL_STATUS.NOT_DUE:
      return '·'
    case FOOD_SAFETY_REPORT_CELL_STATUS.OUT_OF_SERVICE:
      return FOOD_SAFETY_REPORT_OUT_OF_SERVICE_MARK
    case FOOD_SAFETY_REPORT_CELL_STATUS.CLOSED:
      return 'Closed'
    case FOOD_SAFETY_REPORT_CELL_STATUS.LOCKED:
      return '✕'
    case FOOD_SAFETY_REPORT_CELL_STATUS.NONE:
    default:
      return '—'
  }
}

export function foodSafetyReportCellTone(status) {
  switch (status) {
    case FOOD_SAFETY_REPORT_CELL_STATUS.COMPLETED:
    case FOOD_SAFETY_REPORT_CELL_STATUS.LOGGED:
      return 'ok'
    case FOOD_SAFETY_REPORT_CELL_STATUS.MISSED:
      return 'alert'
    case FOOD_SAFETY_REPORT_CELL_STATUS.PARTIAL:
      return 'warn'
    case FOOD_SAFETY_REPORT_CELL_STATUS.LOCKED:
      return 'locked'
    case FOOD_SAFETY_REPORT_CELL_STATUS.OUT_OF_SERVICE:
      return 'paused'
    default:
      return 'muted'
  }
}

export function foodSafetyReportCellTitle(cell) {
  if (!cell) return ''
  const count = Number(cell.notRequiredCount) || 0
  const statusTitle = foodSafetyReportCellStatusTitle(cell)
  const base =
    count > 0
      ? `${
          cell.status === FOOD_SAFETY_REPORT_CELL_STATUS.NONE
            ? ''
            : `${statusTitle} · `
        }${count} marked Not Required`
      : statusTitle
  const exceptions = foodSafetyReportCellExceptionLabel(cell)
  return exceptions ? `${base} · ${exceptions}` : base
}

/**
 * What a screen reader hears for a grid cell, e.g.
 * "Fridges & Freezers, Mon 28 Sep: All 3 due checks done · 1 exception · action recorded".
 */
export function foodSafetyReportCellLabel(cell, { sectionLabel } = {}) {
  if (!cell) return ''
  const day = dayjs(cell.localDate, 'YYYY-MM-DD', true)
  const head = [sectionLabel, day.isValid() ? day.format('ddd D MMM') : null]
    .filter(Boolean)
    .join(', ')
  const body = foodSafetyReportCellTitle(cell)
  return head ? `${head}: ${body}` : body
}

function foodSafetyReportCellStatusTitle(cell) {
  const {
    status,
    dueCount,
    completedCount,
    missedCount,
    logCount,
    stillNeededCount,
  } = cell
  if (status === FOOD_SAFETY_REPORT_CELL_STATUS.LOCKED) {
    return 'Older than Lite history — unlock on Starter'
  }
  if (status === FOOD_SAFETY_REPORT_CELL_STATUS.COMPLETED) {
    if (Number(dueCount) === 1) return '1 due check done'
    return dueCount > 0 ? `All ${dueCount} due checks done` : 'Completed'
  }
  if (status === FOOD_SAFETY_REPORT_CELL_STATUS.MISSED) {
    return missedCount > 0 ? `${missedCount} missed` : 'Missed'
  }
  if (status === FOOD_SAFETY_REPORT_CELL_STATUS.PARTIAL) {
    const still = Number(stillNeededCount) || 0
    if (still > 0 && !(Number(dueCount) > 0)) {
      return still === 1 ? '1 still to log' : `${still} still to log`
    }
    if (Number(dueCount) > 0) {
      const base = `${completedCount} of ${dueCount} done`
      return still > 0 ? `${base} · ${still} still to log` : base
    }
    if (still > 0) {
      return still === 1 ? '1 still to log' : `${still} still to log`
    }
    return 'Partly done'
  }
  if (status === FOOD_SAFETY_REPORT_CELL_STATUS.LOGGED) {
    return logCount === 1 ? '1 log' : `${logCount || 0} logs`
  }
  if (status === FOOD_SAFETY_REPORT_CELL_STATUS.NOT_DUE) {
    return 'Not due yet'
  }
  if (status === FOOD_SAFETY_REPORT_CELL_STATUS.OUT_OF_SERVICE) {
    const n = Number(cell.outOfServiceCount) || 0
    return n > 1 ? `${n} out of service` : 'Out of service'
  }
  if (status === FOOD_SAFETY_REPORT_CELL_STATUS.CLOSED) {
    return 'Closed'
  }
  return 'Nothing due'
}

/**
 * Reports week-grid cell click (29.32): navigate to that section/day, or open
 * Free history paywall for locked cells. Skip future not-due and past empty.
 *
 * @param {{ status?: string, localDate?: string }|null|undefined} cell
 * @param {{ todayLocalDate?: string }} [opts]
 * @returns {'navigate'|'paywall'|null}
 */
export function resolveFoodSafetyReportCellAction(
  cell,
  { todayLocalDate } = {},
) {
  if (!cell?.localDate || !cell.status) return null
  const { status, localDate } = cell
  if (status === FOOD_SAFETY_REPORT_CELL_STATUS.LOCKED) {
    return 'paywall'
  }
  if (status === FOOD_SAFETY_REPORT_CELL_STATUS.NOT_DUE) {
    return null
  }
  if (status === FOOD_SAFETY_REPORT_CELL_STATUS.NONE) {
    const today = todayLocalDate || dayjs().format('YYYY-MM-DD')
    return localDate === today ? 'navigate' : null
  }
  // completed | partial | missed | logged — days with work (or done)
  return 'navigate'
}

/**
 * Dashboard href for a reports cell → section day (parity with Home tiles).
 * @returns {string|null}
 */
export function foodSafetyReportsSectionHref(venueId, section, localDate) {
  if (!venueId || !section || !localDate) return null
  return `/v/${venueId}/food-safety/section/${section}?date=${localDate}`
}

/**
 * ISO Monday for “this week” relative to an anchor date (dashboard local).
 */
export function foodSafetyThisWeekMonday(anchor = dayjs()) {
  const d = dayjs.isDayjs(anchor) ? anchor : dayjs(anchor)
  return d.startOf('isoWeek').format('YYYY-MM-DD')
}

/**
 * Snap any local date to the ISO Monday of that week (Older Reports jump picker).
 * @param {string|Date|import('dayjs').Dayjs|null|undefined} value
 * @returns {string|null} YYYY-MM-DD Monday or null
 */
export function foodSafetyReportsMondayOf(value) {
  if (value == null || value === '') return null
  const d = dayjs.isDayjs(value) ? value : dayjs(value)
  if (!d.isValid()) return null
  return d.startOf('isoWeek').format('YYYY-MM-DD')
}

/**
 * Classify a weekStart Monday as this_week / last_week / older.
 */
export function classifyFoodSafetyReportsWeek(
  weekStart,
  { today = dayjs() } = {},
) {
  if (!weekStart) return null
  const thisMonday = foodSafetyThisWeekMonday(today)
  const lastMonday = shiftFoodSafetyWeekStart(thisMonday, -1)
  if (weekStart === thisMonday) return FOOD_SAFETY_WEEK_PRESET.THIS_WEEK
  if (weekStart === lastMonday) return FOOD_SAFETY_WEEK_PRESET.LAST_WEEK
  return FOOD_SAFETY_WEEK_PRESET.OLDER
}

/**
 * Resolve ◀ / ▶ week shift from the currently loaded weekStart.
 * Free venues: weeks older than Last Week request a paywall (honest, not silent).
 *
 * @returns {{
 *   blocked?: 'future'|'paywall',
 *   tab?: string,
 *   weekStart?: string,
 * }|null}
 */
export function resolveFoodSafetyReportsWeekShift(
  weekStart,
  deltaWeeks,
  { fullHistoryUnlocked = false, today = dayjs() } = {},
) {
  if (!weekStart) return null
  const next = shiftFoodSafetyWeekStart(weekStart, deltaWeeks)
  if (!next) return null
  const thisMonday = foodSafetyThisWeekMonday(today)
  if (next > thisMonday) return { blocked: 'future' }
  const tab = classifyFoodSafetyReportsWeek(next, { today })
  if (tab === FOOD_SAFETY_WEEK_PRESET.OLDER && !fullHistoryUnlocked) {
    return { blocked: 'paywall', weekStart: next, tab }
  }
  return { tab, weekStart: next }
}

/**
 * Calm gap lines for the reports page (missed + still-needed), max a few.
 */
export function formatFoodSafetyReportsGapLines(rows, { maxLines = 4 } = {}) {
  const lines = []
  for (const row of rows || []) {
    const label = row.label || row.section
    const summary = row.weekSummary || {}
    if (Number(summary.missedCount) > 0) {
      lines.push(
        `${label}: ${summary.missedCount} missed${
          summary.missedCount === 1 ? ' check' : ' checks'
        }`,
      )
    }
    if (Number(summary.stillNeededCount) > 0) {
      lines.push(
        summary.stillNeededCount === 1
          ? `${label}: 1 still to log`
          : `${label}: ${summary.stillNeededCount} still to log`,
      )
    }
    if (lines.length >= maxLines) break
  }
  return lines.slice(0, maxLines)
}

/** Quiet week — gap strip isn’t missing; kitchen is clear (this week). */
export const FOOD_SAFETY_REPORTS_GAP_CLEAR = 'Nothing left to log today'

/** Quiet past week — don’t say “today” when viewing Last Week / Older (H10 / 29.26). */
export const FOOD_SAFETY_REPORTS_GAP_CLEAR_PAST_WEEK =
  'Nothing left to log this week'

/**
 * Gap strip cue: amber attention lines, or one calm clear line (19.33 / 29.26).
 * @param {object[]} rows
 * @param {{ weekStart?: string, today?: import('dayjs').Dayjs|string|Date }} [opts]
 * @returns {{ kind: 'attention', lines: string[] } | { kind: 'clear', line: string }}
 */
export function foodSafetyReportsGapCue(
  rows,
  { weekStart, today = dayjs() } = {},
) {
  const lines = formatFoodSafetyReportsGapLines(rows)
  if (lines.length > 0) return { kind: 'attention', lines }
  const thisMonday = foodSafetyThisWeekMonday(today)
  const isCurrentWeek = !weekStart || weekStart === thisMonday
  return {
    kind: 'clear',
    line: isCurrentWeek
      ? FOOD_SAFETY_REPORTS_GAP_CLEAR
      : FOOD_SAFETY_REPORTS_GAP_CLEAR_PAST_WEEK,
  }
}

/**
 * Whether export / older history is unlocked for this venue.
 */
export function isFoodSafetyReportsExportUnlocked(historyAccess) {
  return Boolean(historyAccess?.fullHistoryUnlocked)
}

/** Starter/paid Reports header — no Free upgrade chrome. */
export const FOOD_SAFETY_REPORTS_WEEK_STATUS_PAID =
  'Week status for the kitchen.'

/** Free Reports header — explains the free This/Last Week window. */
export const FOOD_SAFETY_REPORTS_WEEK_STATUS_FREE =
  'Week status for the kitchen — This Week and Last Week stay free.'

/**
 * Plan-aware Reports page subtitle.
 * “Stay free” only on Free; Starter/paid get a neutral week-status line.
 */
export function foodSafetyReportsWeekStatusSubtitle(historyAccess) {
  if (isFoodSafetyReportsExportUnlocked(historyAccess)) {
    return FOOD_SAFETY_REPORTS_WEEK_STATUS_PAID
  }
  return FOOD_SAFETY_REPORTS_WEEK_STATUS_FREE
}

/** Shown once in paywall body — never also hardcode in the modal. */
export const FOOD_SAFETY_REPORTS_PAYWALL_FREE_ASSURANCE =
  'This Week and Last Week stay free. Logging is never interrupted.'

export const FOOD_SAFETY_REPORTS_SAMPLE_PREVIEW_LABEL = 'Preview sample report'
export const FOOD_SAFETY_REPORTS_SAMPLE_PREVIEW_HINT =
  'Opens a fictional kitchen PDF — not your logs. Real export stays on Starter.'

export const FOOD_SAFETY_REPORTS_SAMPLE_EMAIL_LABEL = 'Email me a sample'
export const FOOD_SAFETY_REPORTS_SAMPLE_EMAIL_HINT =
  'Sends the same demo report to your inbox so you can see the email format. Not your real logs.'

function withFoodSafetyPaywallAssurance(body) {
  const base = String(body || '').trim()
  if (!base) return FOOD_SAFETY_REPORTS_PAYWALL_FREE_ASSURANCE
  if (base.includes('This Week and Last Week stay free')) {
    return base.includes('Logging is never interrupted')
      ? base
      : `${base} Logging is never interrupted.`
  }
  return `${base} ${FOOD_SAFETY_REPORTS_PAYWALL_FREE_ASSURANCE}`
}

/**
 * Contextual Starter paywall copy for Older / Download / Email.
 * Prefer API upgradeHint when present (includes older record count).
 * Body always includes free-weeks assurance exactly once (modal must not repeat it).
 */
export function buildFoodSafetyReportsPaywallCopy({
  reason,
  olderRecordCount,
  upgradeHint,
} = {}) {
  const n = Number(olderRecordCount) || 0
  const hint =
    upgradeHint ||
    (n === 1
      ? 'You have 1 older Food Safety record waiting. Starter brings back your full history, plus PDF download and email.'
      : n > 1
      ? `You have ${n} older Food Safety records waiting. Starter brings back your full history, plus PDF download and email.`
      : null)

  if (reason === FOOD_SAFETY_REPORTS_PAYWALL_REASON.DOWNLOAD) {
    return {
      title: 'Hand inspectors a Food Safety PDF',
      body: withFoodSafetyPaywallAssurance(
        hint ||
          'Starter downloads a PDF of the checks you’ve already logged — ready for managers and inspectors.',
      ),
      primaryLabel: 'View plans',
      sampleLabel: FOOD_SAFETY_REPORTS_SAMPLE_PREVIEW_LABEL,
      sampleEmailLabel: FOOD_SAFETY_REPORTS_SAMPLE_EMAIL_LABEL,
    }
  }

  if (reason === FOOD_SAFETY_REPORTS_PAYWALL_REASON.EMAIL) {
    return {
      title: 'Send Food Safety reports straight to your manager',
      body: withFoodSafetyPaywallAssurance(
        hint ||
          'Starter emails your Food Safety report to the manager address you’ve set up, so nobody has to export it by hand.',
      ),
      primaryLabel: 'View plans',
      sampleLabel: FOOD_SAFETY_REPORTS_SAMPLE_PREVIEW_LABEL,
      sampleEmailLabel: FOOD_SAFETY_REPORTS_SAMPLE_EMAIL_LABEL,
    }
  }

  // older
  return {
    title: 'Unlock your full Food Safety history',
    body: withFoodSafetyPaywallAssurance(
      hint ||
        'Starter keeps every older week at your fingertips, plus PDF download and email.',
    ),
    primaryLabel: 'View plans',
    sampleLabel: FOOD_SAFETY_REPORTS_SAMPLE_PREVIEW_LABEL,
    sampleEmailLabel: FOOD_SAFETY_REPORTS_SAMPLE_EMAIL_LABEL,
  }
}

export function foodSafetyReportsExportLockLine(historyAccess) {
  if (isFoodSafetyReportsExportUnlocked(historyAccess)) {
    return 'Download or email this week, or pick a custom range (up to 6 weeks) for one inspection PDF. Emails go to the manager report address in Food Safety setup.'
  }
  const n = Number(historyAccess?.olderRecordCount) || 0
  if (n > 0) {
    return `Starter exports your kitchen’s Food Safety PDF — you already have ${n} older record${
      n === 1 ? '' : 's'
    } waiting. Preview or email yourself a sample below to see what inspectors get.`
  }
  return 'Starter exports your kitchen’s Food Safety PDF for managers and inspectors. Preview or email yourself a sample below to see the format.'
}

/** Tooltip / title when Free export buttons are locked but still clickable. */
export const FOOD_SAFETY_REPORTS_EXPORT_UNLOCK_HINT = 'Unlock on Starter'

/**
 * Free export affordance (19.25): quiet locked labels + unlock hint.
 * Buttons stay clickable so the paywall can open; they must not look like
 * a broken primary that silently fails.
 */
export function foodSafetyReportsExportAffordance(historyAccess) {
  if (isFoodSafetyReportsExportUnlocked(historyAccess)) {
    return {
      locked: false,
      emailLabel: 'Email report',
      downloadLabel: 'Download PDF',
      title: undefined,
      sampleLabel: null,
      sampleHint: null,
      sampleEmailLabel: null,
      sampleEmailHint: null,
    }
  }
  return {
    locked: true,
    emailLabel: 'Unlock email',
    downloadLabel: 'Unlock PDF',
    title: FOOD_SAFETY_REPORTS_EXPORT_UNLOCK_HINT,
    sampleLabel: FOOD_SAFETY_REPORTS_SAMPLE_PREVIEW_LABEL,
    sampleHint: FOOD_SAFETY_REPORTS_SAMPLE_PREVIEW_HINT,
    sampleEmailLabel: FOOD_SAFETY_REPORTS_SAMPLE_EMAIL_LABEL,
    sampleEmailHint: FOOD_SAFETY_REPORTS_SAMPLE_EMAIL_HINT,
  }
}

const FOOD_SAFETY_REPORTS_WEEK_PARAM = 'week'

/** `?week=` from the Reports URL, snapped to its Monday; null when absent/bad. */
export function readFoodSafetyReportsWeekParam(search) {
  const raw = new URLSearchParams(search || '').get(
    FOOD_SAFETY_REPORTS_WEEK_PARAM,
  )
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null
  const d = dayjs(raw, 'YYYY-MM-DD', true)
  if (!d.isValid()) return null
  return d.startOf('isoWeek').format('YYYY-MM-DD')
}

/**
 * Search string with `week` set to the shown week (dropped for this week),
 * keeping any other params.
 */
export function foodSafetyReportsSearchWithWeek(
  search,
  weekStart,
  { todayLocalDate } = {},
) {
  const params = new URLSearchParams(search || '')
  const thisMonday = foodSafetyThisWeekMonday(
    todayLocalDate ? dayjs(todayLocalDate, 'YYYY-MM-DD') : dayjs(),
  )
  if (!weekStart || weekStart === thisMonday) {
    params.delete(FOOD_SAFETY_REPORTS_WEEK_PARAM)
  } else {
    params.set(FOOD_SAFETY_REPORTS_WEEK_PARAM, weekStart)
  }
  const next = params.toString()
  return next ? `?${next}` : ''
}

export function resolveFoodSafetyReportsFetchParams(tab, olderWeekStart) {
  if (tab === FOOD_SAFETY_WEEK_PRESET.THIS_WEEK) {
    return { preset: FOOD_SAFETY_WEEK_PRESET.THIS_WEEK }
  }
  if (tab === FOOD_SAFETY_WEEK_PRESET.LAST_WEEK) {
    return { preset: FOOD_SAFETY_WEEK_PRESET.LAST_WEEK }
  }
  if (tab === FOOD_SAFETY_WEEK_PRESET.OLDER && olderWeekStart) {
    return { weekStart: olderWeekStart }
  }
  return null
}

/** Inclusive inspection PDF window (matches Express). */
export const FOOD_SAFETY_EXPORT_RANGE_MAX_DAYS = 42

/** Error string, or null when the range is ready to export. */
export function validateFoodSafetyExportRange(from, to, { today } = {}) {
  if (!from || !to) return 'Choose a start and end date.'
  const start = dayjs(from, 'YYYY-MM-DD', true)
  const end = dayjs(to, 'YYYY-MM-DD', true)
  if (!start.isValid() || !end.isValid()) return 'Choose a start and end date.'
  if (end.isBefore(start, 'day')) {
    return 'The end date can’t be before the start date.'
  }
  const cap = today ? dayjs(today, 'YYYY-MM-DD', true) : dayjs()
  if (cap.isValid() && end.isAfter(cap, 'day')) {
    return 'The export can’t include a future day.'
  }
  const days = end.diff(start, 'day') + 1
  if (days > FOOD_SAFETY_EXPORT_RANGE_MAX_DAYS) {
    return 'Pick a range of at most 6 weeks (42 days).'
  }
  return null
}
